import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import React, { useMemo, useState } from "react";

interface UploadPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  type: string;
  data: {
    results: [
      {
        status: string;
        rowNumber: number;
        message: string;
        rowData: Record<string, any>;
        errors: Array<{
          errors: string[];
        }>;
      }
    ];
  };
  onConfirm: (payload: any) => void;
  isUpdating: boolean;
}

const excludeKeys = new Set([
  "null",
  "errors",
  "__form_index",
  "Asset NO",
  "System",
  "status",
  "Network",
  "rowNumber",
]);

const typeMapping: Record<string, number> = {
  "Electric Utility": 0,
  "Generator Energy": 1,
  "Renewable Energy": 2,
};

function normalizeRow(item: any) {
  const normalized: Record<string, any> = {};

  // Normalize all keys: trim, remove extra spaces inside, and lowercase
  Object.keys(item).forEach((key) => {
    const cleanKey = key.replace(/\s+/g, "").toLowerCase();
    normalized[cleanKey] = item[key];
  });

  return normalized;
}

export function buildEnergyPayload(
  rawData: any[],
  energyType: number,
  remoteUtilityId: string
) {
  const asset_energy_data = rawData.map((item) => {
    const prev = Number(item[" Previous Reading "] ?? 0);
    const curr = Number(item[" Current Reading "] ?? 0);
    const row = normalizeRow(item);

    return {
      asset_no: row["assetno"]?.trim(),
      device_number: row["devicenumber"]?.trim(),
      units: row["units"]?.trim(),
      current_reading_date: row["currentdate"],
      current_reading: curr,
      previous_reading_date: row["previousdate"],
      previous_reading: prev,
      consumption: curr - prev,
    };
  });

  return {
    asset_energy_type: energyType,
    remote_utility_id: remoteUtilityId,
    asset_energy_data,
  };
}

export const UploadPreviewDialog: React.FC<UploadPreviewDialogProps> = ({
  open,
  onClose,
  type,
  data,
  onConfirm,
  isUpdating,
}) => {
  const [correctedData, setCorrectedData] = useState<
    Record<number, Record<string, any>>
  >({});
  const remoteUtilityId = useRemoteUtilityId();
  const isValidYyyyMmDd = (value: string) => {
    // must match yyyy-mm-dd pattern
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(value)) return false;

    // also ensure actual valid date
    const date = new Date(value);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const processedData = useMemo(() => {
    if (!data?.results) return [];
    return data.results.map((item, index) => ({
      ...item.rowData,
      errors: item.errors,
      rowNumber: item.rowNumber,
      status: item.status,
      __form_index: index,
    }));
  }, [data]);

  // Columns for the generic table
  const columns = useMemo(() => {
    if (processedData.length === 0) return [];

    const baseColumns = Object.keys(processedData[0])
      .filter((key) => !excludeKeys.has(key.trim()))
      .map((key) => ({
        key,
        header: key.trim(),
        type: "custom",
        render: (value: any, row: any) => {
          const fieldType = key.toLowerCase().includes("date")
            ? "date"
            : key.toLowerCase().includes("reading")
            ? "number"
            : "text";

          // Get corrected value if exists, otherwise use original value
          const displayValue = correctedData[row.rowNumber]?.[key] ?? value;

          const handleChange = (val: any) => {
            setCorrectedData((prev) => ({
              ...prev,
              [row.rowNumber]: {
                ...prev[row.rowNumber],
                [key]: val,
              },
            }));
          };

          if (fieldType === "date") {
            return (
              <input
                type="date"
                value={
                  displayValue && isValidYyyyMmDd(displayValue)
                    ? displayValue
                    : ""
                }
                onChange={(e) => handleChange(e.target.value)}
                className="border rounded-md p-1 w-full min-w-[150px]"
              />
            );
          }

          if (fieldType === "number") {
            return (
              <input
                type="number"
                value={displayValue ?? ""}
                onChange={(e) => handleChange(e.target.value)}
                className="border rounded-md p-1 w-full min-w-[100px]"
              />
            );
          }

          return <p className="min-w-[100px]">{displayValue}</p>;
        },
      }));

    baseColumns.push({
      key: "errors",
      header: "Status",
      type: "custom",
      render: (value: string[]) => (
        <div className="text-red-500 min-w-[300px]">
          {value?.map((error, index) => (
            <div key={index} className="text-xs">
              {error}
            </div>
          ))}
        </div>
      ),
    });

    return baseColumns;
  }, [processedData, correctedData]);

  const handleConfirm = () => {
    // Merge original data with corrected data
    const finalData = processedData.map((originalItem) => {
      const correction = correctedData[originalItem.rowNumber];
      const mergedRow = {
        ...originalItem,
        ...correction,
      };

      return mergedRow;
    });

    // // Validate the final data
    // const isValid = finalData.every((row) => {
    //   const previousDateField = Object.keys(row).find((key) =>
    //     key.toLowerCase().includes("previous date")
    //   );
    //   const currentDateField = Object.keys(row).find((key) =>
    //     key.toLowerCase().includes("current date")
    //   );
    //   const previousReadingField = Object.keys(row).find((key) =>
    //     key.toLowerCase().includes("previous reading")
    //   );
    //   const currentReadingField = Object.keys(row).find((key) =>
    //     key.toLowerCase().includes("current reading")
    //   );

    //   if (previousDateField && currentDateField) {
    //     const prevDate = new Date(row[previousDateField]);
    //     const currDate = new Date(row[currentDateField]);
    //     if (prevDate > currDate) {
    //       toast({
    //         title: "Validation Error",
    //         description: `Row ${row.rowNumber}: Previous date cannot be after current date`,
    //         variant: "destructive",
    //       });
    //       return false;
    //     }
    //   }

    //   if (previousReadingField && currentReadingField) {
    //     const prevReading = Number(row[previousReadingField]);
    //     const currReading = Number(row[currentReadingField]);
    //     if (prevReading > currReading) {
    //       toast({
    //         title: "Validation Error",
    //         description: `Row ${row.rowNumber}: Previous reading cannot be greater than current reading`,
    //         variant: "destructive",
    //       });
    //       return false;
    //     }
    //   }

    //   return true;
    // });

    // if (!isValid) return;

    const payload = buildEnergyPayload(
      finalData,
      typeMapping[type],
      remoteUtilityId
    );

    onConfirm(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>Review & Confirm Data</DialogTitle>
          <p className="text-gray-500">
            Review the extracted data below. Fix any errors before confirming.
          </p>
        </DialogHeader>

        <div className="overflow-x-auto max-h-[500px]">
          <GenericTable
            data={processedData}
            columns={columns}
            rowKey="rowNumber"
            emptyMessage="No data to preview"
          />
        </div>

        <DialogFooter className="mt-4 flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={isUpdating}>
            Confirm & Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
